import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import {
  DataList,
  PageHeader,
  RowActions,
  StatusPill,
  useActionRunner,
  type ActionDef,
  type DataListColumn,
} from "@ozpanel/kit";
import { Plus } from "lucide-react";
import { Button, Tabs, TabsList, TabsTrigger, TabsContent, formatBytes, formatDateTime, When } from "@ozpanel/ui";
import { useCreate } from "../../useCreate";
import {
  useAccountsApi,
  type BackupStatusItem,
  type BackupTarget,
  type BackupSchedule,
  type BackupSnapshot,
  type JuiceFSMetaItem,
} from "../../accountsApi";

const statusColumns: DataListColumn<BackupStatusItem>[] = [
  { key: "tenant", label: "Tenant", sortable: true },
  { key: "snapshots", label: "Snapshots", sortable: true },
  {
    key: "failed",
    label: "Failed",
    sortable: true,
    value: (s) => s.failed,
    render: (s) => (s.failed > 0 ? <StatusPill tone="bad">{s.failed}</StatusPill> : "0"),
  },
  { key: "schedules", label: "Schedules", sortable: true },
  {
    key: "last_success",
    label: "Last success",
    sortable: true,
    value: (s) => s.last_success ?? "",
    // An account with no successful backup is not "—", it is unprotected, and must read that way.
    render: (s) =>
      s.last_success ? formatDateTime(s.last_success) : <StatusPill tone="bad">never</StatusPill>,
  },
];

export function BackupsScreen() {
  const api = useAccountsApi();
  const [params, setParams] = useSearchParams();

  const status = useQuery({ queryKey: ["backup-status"], queryFn: api.backupStatus });
  const tenants = useQuery({ queryKey: ["tenants"], queryFn: api.listTenants });

  // Tenant selection lives in the URL, not component state: a backups view of a specific
  // account is a place, so it must survive a refresh and be linkable — the tenant detail
  // page links straight here with ?tenant=<id>.
  const urlTenant = params.get("tenant");
  const tenantId = urlTenant ? Number(urlTenant) : (tenants.data?.items[0]?.id ?? null);
  const setTenantId = (id: number) => setParams((p) => {
    const next = new URLSearchParams(p);
    next.set("tenant", String(id));
    return next;
  });

  const enabled = tenantId !== null;
  const targets = useQuery({ queryKey: ["backup-targets", tenantId], queryFn: () => api.backupTargets(tenantId!), enabled });
  const schedules = useQuery({ queryKey: ["backup-schedules", tenantId], queryFn: () => api.backupSchedules(tenantId!), enabled });
  const snapshots = useQuery({ queryKey: ["backup-snapshots", tenantId], queryFn: () => api.backupSnapshots(tenantId!), enabled });

  const targetRunner = useActionRunner<BackupTarget>();
  const scheduleRunner = useActionRunner<BackupSchedule>();
  const snapshotRunner = useActionRunner<BackupSnapshot>();
  const runRunner = useActionRunner<{ tenantId: number }>();

  const tid = tenantId ?? 0;
  // An action needs a record (id) and files that are there or at least not known to be gone.
  const actionable = (s: BackupSnapshot) => s.id > 0 && s.availability !== "missing";
  const meta = useQuery({ queryKey: ["backup-juicefs-meta"], queryFn: api.juicefsMeta });
  const platform = useQuery({ queryKey: ["backup-platform"], queryFn: api.platformBackups });

  const targetActions: ActionDef<BackupTarget>[] = [
    {
      key: "test",
      label: "Test connection",
      // Logs in with the pinned host key, writes, lists back and removes a probe on the remote.
      available: (t) => t.kind === "sftp",
      run: (t) => api.testBackupTarget(tid, t.id),
      invalidates: () => [["backup-targets", tenantId]],
      successMessage: (t, r) => `${t.name}: ${(r as { detail: string }).detail}`,
    },
    {
      key: "pubkey",
      label: "Copy public key",
      available: (t) => Boolean(t.public_key),
      run: async (t) => {
        await navigator.clipboard.writeText(t.public_key ?? "");
      },
      successMessage: (t) => `Public key of ${t.name} copied — add it to the remote user's authorized_keys`,
    },
    {
      key: "delete",
      label: "Delete",
      danger: "high",
      confirm: (t) => ({
        title: `Delete target “${t.name}”?`,
        body: <p>Snapshots already written to {t.destination} are not removed, but nothing new can be written here.</p>,
        confirmLabel: "Delete target",
      }),
      run: (t) => api.deleteBackupTarget(tid, t.id),
      invalidates: () => [["backup-targets", tenantId]],
      successMessage: (t) => `Target “${t.name}” deleted`,
    },
  ];

  const scheduleActions: ActionDef<BackupSchedule>[] = [
    {
      key: "pause",
      label: "Pause",
      available: (s) => s.active,
      run: (s) => api.setScheduleActive(tid, s.id, false),
      invalidates: () => [["backup-schedules", tenantId]],
      successMessage: () => "Schedule paused",
    },
    {
      key: "resume",
      label: "Resume",
      available: (s) => !s.active,
      run: (s) => api.setScheduleActive(tid, s.id, true),
      invalidates: () => [["backup-schedules", tenantId]],
      successMessage: () => "Schedule resumed",
    },
    {
      key: "delete",
      label: "Delete",
      danger: "high",
      confirm: () => ({ title: "Delete this schedule?", confirmLabel: "Delete schedule" }),
      run: (s) => api.deleteBackupSchedule(tid, s.id),
      invalidates: () => [["backup-schedules", tenantId]],
      successMessage: () => "Schedule deleted",
    },
  ];

  const snapshotActions: ActionDef<BackupSnapshot>[] = [
    {
      key: "verify",
      label: "Verify",
      // danger low — it only re-reads and re-hashes. Worth surfacing prominently: a backup
      // nobody verified is a backup nobody can rely on. Runs on the node holding the files.
      available: actionable,
      run: (s) => api.verifySnapshot(tid, s.id),
      successMessage: (_s, r) => {
        const res = r as { verified: boolean; parts_checked: number; problems?: string[]; node?: string };
        const on = res.node ? ` on ${res.node}` : "";
        return res.verified
          ? `Verified${on} — ${res.parts_checked} part(s) match their recorded hashes`
          : `Verification FAILED${on}: ${(res.problems ?? []).join("; ")}`;
      },
    },
    {
      key: "restore-dry",
      label: "Restore (dry run)",
      available: actionable,
      run: (s) => api.restoreSnapshot(tid, s.id, true),
      successMessage: (_s, r) => {
        const res = r as { done?: string[]; skipped?: string[] };
        return `Dry run: would restore ${(res.done ?? []).join(", ") || "nothing"}`;
      },
    },
    {
      key: "restore",
      label: "Restore for real",
      danger: "high",
      available: actionable,
      confirm: (s) => ({
        title: `Restore ${s.name} over live data?`,
        body: (
          <p>
            This overwrites the account's current files and databases with the contents of this
            snapshot ({formatBytes(s.bytes)}, taken <When value={s.started_at} />). Run the dry
            run first if you have not. This cannot be undone.
          </p>
        ),
        confirmLabel: "Overwrite live data",
      }),
      run: (s) => api.restoreSnapshot(tid, s.id, false),
      invalidates: () => [["backup-snapshots", tenantId]],
      successMessage: () => "Restore completed",
    },
    {
      key: "delete",
      label: "Delete",
      danger: "high",
      available: (s) => s.id > 0 && s.availability !== "unknown",
      confirm: (s) => ({
        title: `Delete snapshot ${s.name}?`,
        body: <p>The archive files are removed from {s.held_by ?? s.node ?? "the node that holds them"}'s disk. This cannot be undone.</p>,
        confirmLabel: "Delete snapshot",
      }),
      run: (s) => api.deleteSnapshot(tid, s.id),
      invalidates: () => [["backup-snapshots", tenantId], ["backup-status"]],
      successMessage: (s) => `Snapshot ${s.name} deleted`,
    },
  ];

  const runNow: ActionDef<{ tenantId: number }> = {
    key: "run",
    label: "Run backup now",
    run: (o) => api.runBackup(o.tenantId),
    invalidates: () => [["backup-snapshots", tenantId], ["backup-status"]],
    successMessage: (_o, r) => {
      const res = r as { snapshot: string; bytes: number };
      return `Snapshot ${res.snapshot} written (${formatBytes(res.bytes)})`;
    },
  };

  const targetColumns: DataListColumn<BackupTarget>[] = [
    { key: "name", label: "Name", sortable: true },
    { key: "kind", label: "Kind", sortable: true },
    { key: "destination", label: "Destination" },
    {
      key: "offsite",
      label: "Offsite",
      value: (t) => (t.offsite ? "yes" : "no"),
      // A local-only target is a real risk the server itself warns about — surface it.
      render: (t) => <StatusPill tone={t.offsite ? "ok" : "warn"}>{t.offsite ? "yes" : "same server"}</StatusPill>,
    },
    {
      key: "connection",
      label: "Connection",
      value: (t) => t.connection ?? "",
      // An sftp target that never passed a test proves nothing — it must not read as working.
      render: (t) =>
        t.kind !== "sftp" ? (
          "—"
        ) : (
          <span title={t.warning ?? (t.tested_at ? `tested ${formatDateTime(t.tested_at)}` : "")}>
            <StatusPill tone={t.connection === "ok" ? "ok" : t.connection === "failed" ? "bad" : "warn"}>
              {t.connection ?? "untested"}
            </StatusPill>
          </span>
        ),
    },
  ];

  const scheduleColumns: DataListColumn<BackupSchedule>[] = [
    { key: "target", label: "Target", sortable: true },
    { key: "what", label: "What" },
    { key: "cron", label: "Cron" },
    { key: "keep", label: "Keep", sortable: true },
    {
      key: "active",
      label: "Active",
      sortable: true,
      value: (s) => (s.active ? "active" : "paused"),
      render: (s) => <StatusPill tone={s.active ? "ok" : "neutral"}>{s.active ? "active" : "paused"}</StatusPill>,
    },
    { key: "last_run_at", label: "Last run", sortable: true, value: (s) => s.last_run_at ?? "", render: (s) => <When value={s.last_run_at} /> },
  ];

  const snapshotColumns: DataListColumn<BackupSnapshot>[] = [
    { key: "name", label: "Name", sortable: true },
    {
      key: "status",
      label: "Status",
      sortable: true,
      value: (s) => s.status,
      render: (s) => (
        <StatusPill tone={s.status === "ok" || s.status === "complete" ? "ok" : "bad"}>{s.status}</StatusPill>
      ),
    },
    { key: "what", label: "What" },
    { key: "bytes", label: "Size", sortable: true, value: (s) => s.bytes, render: (s) => formatBytes(s.bytes) },
    { key: "started_at", label: "Started", sortable: true, value: (s) => s.started_at, render: (s) => <When value={s.started_at} /> },
    // Backups stay on the node that took them; every node's console discovers them all.
    { key: "node", label: "Stored on", sortable: true, value: (s) => s.held_by ?? s.node ?? "" },
    {
      key: "availability",
      label: "Files",
      sortable: true,
      value: (s) => s.availability ?? "",
      render: (s) => (
        <span title={s.warning ?? s.note ?? ""}>
          <StatusPill tone={s.availability === "available" ? "ok" : s.availability === "missing" ? "bad" : "neutral"}>
            {s.availability === "available" ? "present" : s.availability === "missing" ? "missing" : "unknown"}
          </StatusPill>
        </span>
      ),
    },
  ];

  const metaColumns: DataListColumn<JuiceFSMetaItem>[] = [
    { key: "node", label: "Node", sortable: true },
    { key: "volume", label: "Volume", sortable: true },
    { key: "newest", label: "Newest dump", sortable: true, value: (m) => m.newest, render: (m) => <When value={m.newest} /> },
    { key: "newest_bytes", label: "Size", sortable: true, value: (m) => m.newest_bytes, render: (m) => formatBytes(m.newest_bytes) },
    { key: "count", label: "Kept", sortable: true, render: (m) => `${m.count} (${formatBytes(m.bytes)})` },
    { key: "oldest", label: "Oldest", sortable: true, value: (m) => m.oldest, render: (m) => <When value={m.oldest} /> },
    {
      key: "last_proven",
      label: "Restore proven",
      value: (m) => m.last_proven ?? "",
      // A backup is only what it can restore: never proven reads as a problem, not a blank.
      render: (m) => (m.last_proven ? m.last_proven + " UTC" : <StatusPill tone="bad">never</StatusPill>),
    },
    {
      key: "availability",
      label: "State",
      render: (m) => (
        <span title={m.note ?? m.path}>
          <StatusPill tone={m.damaged > 0 ? "bad" : m.availability === "available" ? "ok" : "neutral"}>
            {m.damaged > 0 ? `${m.damaged} damaged` : m.availability === "available" ? "reporting" : "unknown"}
          </StatusPill>
        </span>
      ),
    },
  ];

  // Platform archives are daily and verified by reading back; there is no restore drill column.
  const platformColumns = metaColumns.filter((c) => c.key !== "last_proven" && c.key !== "volume");

  // Restoring platform data from an archive on one node (it stays there; the server routes the restore).
  const [restoreNode, setRestoreNode] = React.useState<JuiceFSMetaItem | null>(null);
  const platformRestore = useCreate({
    title: `Restore platform data from ${restoreNode?.node ?? "a node"}`,
    description:
      "Certificates and their keys, Caddy state, mail-sync — restored over the shared volume, so every node sees it. A real " +
      "restore first archives what is there now, so it can be undone. Run it as a dry run first to see exactly what it writes.",
    submitLabel: "Restore",
    fields: [
      {
        key: "archive",
        label: "Archive",
        required: true,
        options: (restoreNode?.archives ?? []).map((a) => ({ value: a, label: a })),
        defaultValue: restoreNode?.archives?.[0],
      },
      { key: "paths", label: "Paths (optional)", placeholder: "certs/oztag.click, caddy", help: "Comma-separated, relative to the volume. Empty restores everything in the archive." },
      { key: "dry_run", label: "Dry run", type: "bool", defaultValue: true, help: "List what would be written; write nothing." },
    ],
    run: async (v) => {
      const r = await api.restorePlatform({
        node: restoreNode?.node ?? "",
        archive: String(v.archive),
        paths: String(v.paths ?? "").split(",").map((p) => p.trim()).filter(Boolean),
        dry_run: Boolean(v.dry_run),
      });
      setRestoreResult(r);
      return r;
    },
    invalidates: [["backup-platform"]],
    success: (v) => (v.dry_run ? "Dry run complete — what it would write is shown below" : "Platform data restored — details below"),
  });
  const [restoreResult, setRestoreResult] = React.useState<
    { node: string; entries: number; sample?: string[]; safety_archive?: string; note?: string; dry_run: boolean } | null
  >(null);
  const platformRunner = useActionRunner<JuiceFSMetaItem>();
  const platformActions: ActionDef<JuiceFSMetaItem>[] = [
    {
      key: "restore",
      label: "Restore…",
      danger: "high",
      available: (m) => m.availability === "available" && (m.archives?.length ?? 0) > 0,
      run: async (m) => {
        setRestoreNode(m);
        platformRestore.open();
      },
    },
  ];

  const [createdKey, setCreatedKey] = React.useState<{ name: string; public_key?: string; next?: string } | null>(null);
  const newTarget = useCreate({
    title: "New backup target",
    description:
      "Where snapshots are written. Local: a path on the node that takes the backup (it stays there). SFTP: an off-site " +
      "server — user@host:/path. For SFTP the server's host key is pinned now, and a key pair is generated unless you give a password.",
    submitLabel: "Add target",
    fields: [
      { key: "name", label: "Name", required: true, placeholder: "offsite-daily" },
      { key: "kind", label: "Kind", options: [{ value: "local", label: "Local disk" }, { value: "sftp", label: "SFTP (off-site)" }], defaultValue: "local" },
      { key: "destination", label: "Destination", required: true, placeholder: "/var/backups/ozpanel/local  ·  or  backup@storage.example:/backups" },
      { key: "port", label: "SFTP port", type: "int", defaultValue: 22, help: "SFTP only. Hetzner storage boxes use 23." },
      { key: "password", label: "SFTP password", type: "password", help: "SFTP only, optional. Leave empty to have a key pair generated (recommended)." },
      { key: "offsite", label: "Offsite", type: "bool", help: "Local only: mark if this path is on different hardware. SFTP targets are off-site." },
    ],
    run: async (v) => {
      const r = (await api.createBackupTarget(tid, { ...v, kind: v.kind || "local", port: Number(v.port) || 22 })) as {
        name: string;
        public_key?: string;
        next?: string;
      };
      if (r.public_key || r.next) setCreatedKey(r);
      return r;
    },
    invalidates: [["backup-targets", tenantId]],
    success: (v) => `Target ${v.name} added`,
  });
  const newSchedule = useCreate({
    title: "New backup schedule",
    description: "Written to cron.d — it runs whether or not the panel is running.",
    submitLabel: "Add schedule",
    fields: [
      {
        key: "target_id",
        label: "Target",
        required: true,
        options: (targets.data?.items ?? []).map((t) => ({ value: String(t.id), label: t.name })),
      },
      { key: "cron", label: "Schedule (cron)", defaultValue: "17 3 * * *", help: "Five fields, e.g. 17 3 * * * = 03:17 daily." },
      { key: "keep", label: "Keep", type: "int", defaultValue: 7, help: "How many snapshots to retain before the oldest is purged." },
      { key: "what", label: "What to back up", defaultValue: "files,databases,config" },
    ],
    run: (v) => api.createBackupSchedule(tid, { ...v, target_id: Number(v.target_id), keep: Number(v.keep) }),
    invalidates: [["backup-schedules", tenantId]],
    success: () => "Schedule added",
  });

  const selectedTenant = tenants.data?.items.find((t) => t.id === tenantId);

  return (
    <div>
      <PageHeader
        title="Backups" subtitle={
          status.data
            ? `${status.data.total} accounts · ${status.data.unprotected.length} with no successful backup`
            : undefined
        }
        actions={
          <div className="flex items-center gap-2">
            <select
              aria-label="Tenant" className="rounded-md border border-neutral-300 px-2 py-1.5 text-sm" value={tenantId ?? ""}
              onChange={(e) => setTenantId(Number(e.target.value))}
            >
              {tenants.data?.items.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            <Button
              disabled={!enabled || runRunner.isPending}
              onClick={() => runRunner.start(runNow, { tenantId: tid })}
            >
              {runRunner.isPending ? "Running…" : "Run backup now"}
            </Button>
          </div>
        }
      />

      {status.data && status.data.unprotected.length > 0 && (
        <p className="mb-4 rounded-md border border-[color-mix(in_oklab,var(--ozp-warn)_35%,transparent)] bg-warnsoft p-3 text-sm text-warn   dark:text-amber-300">
          No successful backup on record for: {status.data.unprotected.join(", ")}
        </p>
      )}

      <Tabs defaultValue="status">
        <TabsList>
          <TabsTrigger value="status">Status</TabsTrigger>
          <TabsTrigger value="targets">Targets</TabsTrigger>
          <TabsTrigger value="schedules">Schedules</TabsTrigger>
          <TabsTrigger value="snapshots">Snapshots</TabsTrigger>
          <TabsTrigger value="juicefs-meta">JuiceFS metadata</TabsTrigger>
          <TabsTrigger value="platform">Platform data</TabsTrigger>
        </TabsList>

        <TabsContent value="status">
          <DataList
            items={status.data?.items ?? []}
            columns={statusColumns}
            getRowKey={(s) => String(s.tenant_id)}
            onRowClick={(s) => setTenantId(s.tenant_id)}
            searchable
            loading={status.isLoading}
            error={status.error}
            errorContext="backup status" emptyMessage="No accounts." />
        </TabsContent>

        <TabsContent value="targets">
          <div className="mb-3 flex justify-end">
            <Button size="sm" variant="soft" disabled={!enabled} onClick={newTarget.open}>
              <Plus size={13} />
              Add target
            </Button>
          </div>
          {createdKey && (
            <div className="mb-3 rounded-md border border-neutral-300 p-3 text-[12.5px]">
              <p className="font-medium">{createdKey.name}: {createdKey.next}</p>
              {createdKey.public_key && (
                <pre className="mt-2 overflow-auto whitespace-pre-wrap break-all font-mono text-[11.5px]">{createdKey.public_key}</pre>
              )}
            </div>
          )}
          <DataList
            items={targets.data?.items ?? []}
            columns={targetColumns}
            getRowKey={(t) => String(t.id)}
            rowActions={(t) => <RowActions actions={targetActions} obj={t} runner={targetRunner} />}
            loading={targets.isLoading}
            error={targets.error}
            errorContext="backup targets" emptyMessage={`No backup targets for ${selectedTenant?.name ?? "this tenant"}.`}
          />
        </TabsContent>

        <TabsContent value="schedules">
          <div className="mb-3 flex justify-end">
            <Button size="sm" variant="soft" disabled={!enabled} onClick={newSchedule.open}>
              <Plus size={13} />
              Add schedule
            </Button>
          </div>
          <DataList
            items={schedules.data?.items ?? []}
            columns={scheduleColumns}
            getRowKey={(s) => String(s.id)}
            rowActions={(s) => <RowActions actions={scheduleActions} obj={s} runner={scheduleRunner} />}
            loading={schedules.isLoading}
            error={schedules.error}
            errorContext="backup schedules" emptyMessage={`No schedules for ${selectedTenant?.name ?? "this tenant"}.`}
          />
        </TabsContent>

        <TabsContent value="snapshots">
          <DataList
            items={snapshots.data?.items ?? []}
            columns={snapshotColumns}
            getRowKey={(s) => (s.id > 0 ? String(s.id) : `${s.held_by ?? s.node}:${s.name}`)}
            rowActions={(s) => <RowActions actions={snapshotActions} obj={s} runner={snapshotRunner} />}
            searchable
            loading={snapshots.isLoading}
            error={snapshots.error}
            errorContext="snapshots" emptyMessage={`No snapshots for ${selectedTenant?.name ?? "this tenant"} yet.`}
          />
        </TabsContent>

        <TabsContent value="juicefs-meta">
          {meta.data?.note && <p className="mb-3 text-sm text-neutral-500">{meta.data.note}</p>}
          <DataList
            items={meta.data?.items ?? []}
            columns={metaColumns}
            getRowKey={(m) => `${m.node}:${m.volume}`}
            loading={meta.isLoading}
            error={meta.error}
            errorContext="JuiceFS metadata backups" emptyMessage="No node has reported a JuiceFS metadata dump yet."
          />
        </TabsContent>

        <TabsContent value="platform">
          {platform.data?.note && <p className="mb-3 text-sm text-neutral-500">{platform.data.note}</p>}
          <DataList
            items={platform.data?.items ?? []}
            columns={platformColumns}
            getRowKey={(m) => m.node}
            rowActions={(m) => <RowActions actions={platformActions} obj={m} runner={platformRunner} />}
            loading={platform.isLoading}
            error={platform.error}
            errorContext="platform backups" emptyMessage="No node has reported a platform archive yet."
          />
          {restoreResult && (
            <div className="mt-3 rounded-md border border-neutral-300 p-3 text-[12.5px]">
              <p className="font-medium">
                {restoreResult.dry_run ? "Dry run" : "Restored"} on {restoreResult.node}: {restoreResult.entries} entr(ies)
                {restoreResult.safety_archive ? ` · state before the restore kept in ${restoreResult.safety_archive}` : ""}
              </p>
              {restoreResult.note && <p className="mt-1 text-neutral-500">{restoreResult.note}</p>}
              {restoreResult.sample && (
                <pre className="mt-2 max-h-48 overflow-auto font-mono text-[11.5px]">{restoreResult.sample.join("\n")}</pre>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {targetRunner.dialog}
      {scheduleRunner.dialog}
      {snapshotRunner.dialog}
      {runRunner.dialog}
      {platformRunner.dialog}
      {platformRestore.dialog}
      {newTarget.dialog}
      {newSchedule.dialog}
    </div>
  );
}
